// Funciones puras de la vista móvil del calendario de eventos (Mes y Semana).
// Sin React ni BD: reparto de eventos en carriles, posición por horas y barras
// de los multievento en el mes.

import { finSinDefinir } from './tareasPresupuesto.js';
import { fechaLocalISO, lunesDeLaSemana } from './pantallasUnDia.js';

// Eje de horas fijo de la vista Semana (se amplía si algún evento se sale)
export const HORA_DESDE = 7;
export const HORA_HASTA = 22;
// Duración provisional cuando no hay hora de fin (o fin = inicio)
export const HORAS_FIN_PROVISIONAL = 2;
// Duración mínima pintada de un bloque (para que se pueda tocar)
const DURACION_MINIMA = 0.5;
// Con más carriles que este tope se ven tope - 1 y el resto va en un "+N"
export const TOPE_CARRILES = 3;

export const LETRAS_DIA = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];
export const NOMBRES_DIA = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];
export const NOMBRES_MES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

// 'HH:MM' -> horas decimales (9.5); null si no es una hora
export const horaDecimal = (valor) => {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(valor || '').trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 24 || min > 59) return null;
  return h + min / 60;
};

export const sumarDias = (fecha, n) => {
  const f = new Date(fecha);
  f.setDate(f.getDate() + n);
  return f;
};

// Los 7 días (lunes a domingo) de la semana de `fecha`
export const diasDeLaSemana = (fecha) => {
  const lunes = lunesDeLaSemana(fecha);
  return Array.from({ length: 7 }, (_, i) => sumarDias(lunes, i));
};

// Semanas completas (lunes a domingo) que cubren el mes de `fecha`
export const semanasDelMes = (fecha) => {
  const primero = new Date(fecha.getFullYear(), fecha.getMonth(), 1);
  const ultimo = new Date(fecha.getFullYear(), fecha.getMonth() + 1, 0);
  const semanas = [];
  let lunes = lunesDeLaSemana(primero);
  while (lunes <= ultimo) {
    semanas.push(Array.from({ length: 7 }, (_, i) => sumarDias(lunes, i)));
    lunes = sumarDias(lunes, 7);
  }
  return semanas;
};

// ¿Algún día de `dias` cae en el mes de `fechaMes`?
export const semanaTocaMes = (dias, fechaMes) => dias.some(
  d => d.getFullYear() === fechaMes.getFullYear() && d.getMonth() === fechaMes.getMonth()
);

// "5 al 11 de octubre" / "28 de septiembre al 4 de octubre" / con año si cambia
export const textoRangoSemana = (dias) => {
  const a = dias[0];
  const b = dias[dias.length - 1];
  if (a.getFullYear() !== b.getFullYear()) {
    return `${a.getDate()} de ${NOMBRES_MES[a.getMonth()]} de ${a.getFullYear()} al ${b.getDate()} de ${NOMBRES_MES[b.getMonth()]} de ${b.getFullYear()}`;
  }
  if (a.getMonth() !== b.getMonth()) {
    return `${a.getDate()} de ${NOMBRES_MES[a.getMonth()]} al ${b.getDate()} de ${NOMBRES_MES[b.getMonth()]}`;
  }
  return `${a.getDate()} al ${b.getDate()} de ${NOMBRES_MES[a.getMonth()]}`;
};

export const diaISO = (fecha) => fechaLocalISO(fecha);
export const diaDeEvento = (ev) => String(ev?.fecha_evento || '').split('T')[0];
export const claveEvento = (ev) => `${ev?.id || ''}|${ev?.jornada_id || ''}|${ev?.fecha_evento || ''}`;
export const esConfirmado = (ev) => ev?.estado === 'confirmado';
export const esJornada = (ev) => !!(ev?.es_multi_evento || ev?.es_jornada_multi);

// Nombre corto del cliente: la primera palabra (y la segunda si la primera es
// muy corta, "El Corte", "La Caixa"), sin puntuación suelta
export const nombreCorto = (nombre, max = 14) => {
  const palabras = String(nombre || '').replace(/[,;]/g, ' ').split(/\s+/).filter(Boolean);
  if (palabras.length === 0) return 'Evento';
  let texto = palabras[0];
  if (texto.length <= 3 && palabras[1]) texto = `${texto} ${palabras[1]}`;
  return texto.length > max ? `${texto.slice(0, max - 1)}.` : texto;
};

// Tramo horario de un evento en horas decimales. Sin hora de inicio: null.
// Sin hora de fin o fin igual al inicio: fin provisional de 2 h (mismo
// criterio que finSinDefinir). Si el fin es anterior al inicio (pasa de la
// medianoche) se corta a las 24:00.
export const tramoEvento = (ev) => {
  const a = horaDecimal(ev?.hora_inicio);
  if (a === null) return null;
  const provisional = finSinDefinir(ev?.hora_inicio, ev?.hora_fin);
  let b = provisional ? a + HORAS_FIN_PROVISIONAL : horaDecimal(ev?.hora_fin);
  if (b === null) b = a + HORAS_FIN_PROVISIONAL;
  if (b < a) b = 24;
  return { a, b: Math.min(b, 24), provisional };
};

// Hora decimal -> 'HH:MM'
export const textoHora = (h) => {
  const total = Math.round(h * 60);
  const hh = Math.floor(total / 60);
  const mm = total % 60;
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
};

// Eje de horas de una semana: 07:00-22:00, ampliado solo si algún evento
// empieza antes o acaba después
export const ejeHoras = (tramos) => {
  let desde = HORA_DESDE;
  let hasta = HORA_HASTA;
  tramos.forEach(t => {
    if (!t) return;
    desde = Math.min(desde, Math.floor(t.a));
    hasta = Math.max(hasta, Math.ceil(Math.max(t.b, t.a + DURACION_MINIMA)));
  });
  return { desde: Math.max(0, desde), hasta: Math.min(24, hasta) };
};

// Reparte los eventos de un día en grupos de solapes y, dentro de cada grupo,
// en carriles: primero los confirmados, luego el resto; a igualdad, el que
// empieza antes y, si empiezan a la vez, el más largo. `items` lleva
// { a, b, conf } y lo que se quiera arrastrar. Devuelve grupos
// { items: [{ ...item, carril }], n: número de carriles }.
export const repartir = (items) => {
  const lista = items
    .map(x => ({ ...x, b: Math.max(x.b, x.a + DURACION_MINIMA) }))
    .sort((x, y) => x.a - y.a);
  const grupos = [];
  lista.forEach(x => {
    const g = grupos[grupos.length - 1];
    if (g && x.a < g.fin) {
      g.items.push(x);
      g.fin = Math.max(g.fin, x.b);
    } else {
      grupos.push({ items: [x], fin: x.b });
    }
  });
  grupos.forEach(g => {
    const orden = g.items.slice().sort((x, y) => {
      if (x.conf !== y.conf) return x.conf ? -1 : 1;
      return (x.a - y.a) || ((y.b - y.a) - (x.b - x.a));
    });
    const carriles = [];
    orden.forEach(x => {
      let i = 0;
      while (carriles[i] && carriles[i].some(o => x.a < o.b && o.a < x.b)) i += 1;
      if (!carriles[i]) carriles[i] = [];
      carriles[i].push(x);
      x.carril = i;
    });
    g.n = carriles.length;
  });
  return grupos;
};

// Bloques a pintar de un día a partir de `repartir`: con más de TOPE_CARRILES
// carriles se ven TOPE_CARRILES - 1 y el resto va en un bloque "+N" que ocupa
// el último hueco. Cada bloque: { tipo: 'evento'|'mas', a, b, carril, partes,
// item?, ocultos?, claves? }
export const bloquesDelDia = (items) => {
  const bloques = [];
  repartir(items).forEach(g => {
    const partes = Math.min(g.n, TOPE_CARRILES);
    const ocultos = [];
    g.items.forEach(x => {
      if (g.n > TOPE_CARRILES && x.carril >= TOPE_CARRILES - 1) {
        ocultos.push(x);
        return;
      }
      bloques.push({ tipo: 'evento', a: x.a, b: x.b, carril: x.carril, partes, item: x });
    });
    if (ocultos.length) {
      bloques.push({
        tipo: 'mas',
        a: Math.min(...ocultos.map(x => x.a)),
        b: Math.max(...ocultos.map(x => x.b)),
        carril: TOPE_CARRILES - 1,
        partes: TOPE_CARRILES,
        ocultos: ocultos.length,
        // El panel del "+N" enseña todo el grupo que coincide en la franja
        items: g.items,
      });
    }
  });
  return bloques;
};

// Barras de los multievento en una semana del mes. `dias`: los 7 días;
// `enMes(d)`: si el día es del mes que se ve (los de fuera no se pintan);
// `jornadas`: todos los eventos de jornada cargados (ya filtrados; los de
// otras semanas sirven para saber si la barra sigue). Una barra por tramo de días
// seguidos; los multievento que no coinciden comparten carril.
// Devuelve { carriles: n, barras: [{ carril, desde, hasta, conf, texto,
// sigueAntes, sigueDespues, clave }] } con desde/hasta como índice de columna 0-6.
export const barrasMultieventoSemana = (dias, enMes, jornadas) => {
  const isoDias = dias.map(diaISO);
  const porPresupuesto = new Map();
  jornadas.forEach(ev => {
    const col = isoDias.indexOf(diaDeEvento(ev));
    if (col < 0 || !enMes(dias[col])) return;
    const clave = ev.presupuesto_id || ev.id;
    if (!porPresupuesto.has(clave)) porPresupuesto.set(clave, { clave, ev, cols: new Set() });
    porPresupuesto.get(clave).cols.add(col);
  });
  const multis = Array.from(porPresupuesto.values()).map(m => {
    const cols = Array.from(m.cols).sort((x, y) => x - y);
    return { ...m, cols, ini: cols[0], fin: cols[cols.length - 1] };
  }).sort((x, y) => (x.ini - y.ini) || ((y.fin - y.ini) - (x.fin - x.ini)));

  // Carriles: un multievento entra en el primero que ya ha terminado antes
  const finCarril = [];
  const barras = [];
  multis.forEach(m => {
    let carril = finCarril.findIndex(f => f < m.ini);
    if (carril < 0) {
      carril = finCarril.length;
      finCarril.push(m.fin);
    } else {
      finCarril[carril] = m.fin;
    }
    const conf = esConfirmado(m.ev);
    const sufijo = m.ev.total_jornadas ? ` · ${m.ev.total_jornadas} días` : '';
    const texto = `${nombreCorto(m.ev.cliente_nombre)}${sufijo}`;
    // Un tramo por cada racha de días seguidos, cada uno con su nombre
    let tramo = null;
    m.cols.forEach(c => {
      if (tramo && c === tramo.hasta + 1) {
        tramo.hasta = c;
      } else {
        if (tramo) barras.push(tramo);
        tramo = { carril, desde: c, hasta: c, conf, clave: m.clave, texto };
      }
    });
    if (tramo) barras.push(tramo);
  });
  // Puntas redondeadas salvo donde el multievento sigue en la semana de al
  // lado (la barra continúa)
  const diasPorClave = new Map();
  jornadas.forEach(ev => {
    const clave = ev.presupuesto_id || ev.id;
    if (!diasPorClave.has(clave)) diasPorClave.set(clave, new Set());
    diasPorClave.get(clave).add(diaDeEvento(ev));
  });
  const antes = diaISO(sumarDias(dias[0], -1));
  const despues = diaISO(sumarDias(dias[6], 1));
  barras.forEach(b => {
    const set = diasPorClave.get(b.clave) || new Set();
    b.sigueAntes = b.desde === 0 && set.has(antes) && enMes(sumarDias(dias[0], -1));
    b.sigueDespues = b.hasta === 6 && set.has(despues) && enMes(sumarDias(dias[6], 1));
  });
  return { carriles: finCarril.length, barras };
};

// Colores de la vista móvil: confirmado relleno verde; sin confirmar claro con
// borde discontinuo; jornada de multievento en morado (mismo criterio)
export const COLORES_MOVIL = {
  confirmado: { backgroundColor: '#157A58', color: '#FFFFFF' },
  sinConfirmar: { backgroundColor: '#F4F7F5', color: '#1F2A26', border: '1.5px dashed #6B7872' },
  jornadaConfirmada: { backgroundColor: '#6D4BB8', color: '#FFFFFF' },
  jornadaSinConfirmar: { backgroundColor: '#F3EFFC', color: '#4A2F96', border: '1.5px dashed #7B5CC4' },
  grupo: { backgroundColor: '#3B4A44', color: '#FFFFFF' },
};

export const estiloEventoMovil = (ev) => {
  if (esJornada(ev)) return esConfirmado(ev) ? COLORES_MOVIL.jornadaConfirmada : COLORES_MOVIL.jornadaSinConfirmar;
  return esConfirmado(ev) ? COLORES_MOVIL.confirmado : COLORES_MOVIL.sinConfirmar;
};
